import { Box, Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, makeStyles, Typography, Paper } from "@material-ui/core";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import Pagination from "@material-ui/lab/Pagination";
import { PrintOutlined } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import validateDateAndFormat from "utils/validateDateAndFormat";
import fileDownload from "js-file-download";
import DateRangePickerACCB from "./DateRangePickerACCB";
import { BAC_CATEGORY_OPTIONS, getBacFinalAwardReport, printBacFinalAwardReport } from "./BacReportApi";
import GlobalSearch from "../../Components/GlobalSearch/GlobalSearch";

const PAGE_SIZE = 10;

const useStyles = makeStyles((theme) => ({
  filtersContainer: {
    padding: '1rem',
    borderBottom: '1px solid black',
    display: 'flex',
    gap: '15px',
    justifyContent: 'center',
    margin: 'auto',
    // Below md (the dashboard's mobile layout) let the filters wrap instead of
    // squeezing the dropdowns and dates into one row
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  // Desktop caps each dropdown at 22% of the row; below md it takes a row of
  // its own on phones, or shares one on wider screens
  filterSelect: {
    maxWidth: '22%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'none',
      flex: '1 1 160px',
    },
  },
  buttonPrint: {
    borderRadius: '0.5rem',
    border: '1px solid #AAAFB9',
    background: '#fff',
    padding: '0.5rem 1rem',
  },
  tableHeadItem: {
    color: TEXT.strong,
    fontWeight: 600,
    wordBreak: 'break-word',
    fontSize: '0.875rem',
  },
  tableRowValue: {
    color: TEXT.strong,
    fontSize: '0.875rem',
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',
  },
  valuePaper: {
    padding: "1rem 8px",
    boxShadow: "none",
    width: "100%",
    border: "1px solid #E0E0E0",
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
    },
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '1.5rem 8px',
  },
}));

export default function BacFinalAwardReport() {
  const classes = useStyles();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [awards, setAwards] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState("");
  const [printing, setPrinting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);


  const tableHeadings = [
    'S.No.', 'Case No.', 'Category', 'Case Title', 'Arbitrator(s)',
    'Date of Award', 'Award in Favour of', 'Award Value (INR)', 'Applicable Stamp Duty (INR)',
  ];

  const formatArbitrators = (arbitrators) => {
    if (!arbitrators || arbitrators.length === 0) return '-';
    return arbitrators.map((a) => `${a.name}` + (a?.role ? ` (${a.role})` : '')).join('\n');
  };

  const processResponse = (item, index, page) => {
    return [
      (page - 1) * PAGE_SIZE + index + 1,
      item?.case_id,
      item?.category || '-',
      item?.case_title,
      formatArbitrators(item?.arbitrators),
      item?.date_of_award,
      item?.award_in_favour_of || '-',
      item?.award_value || '-',
      item?.applicable_stamp_duty || '-',
    ];
  };

  const isDateRangeInvalid = startDate && endDate && new Date(endDate) < new Date(startDate);

  const reload = (page, searchText = search) => {
    if (isDateRangeInvalid) {
      setAwards([]);
      setTotalCount(0);
      return;
    }
    setLoading(true);
    getBacFinalAwardReport(
      category,
      validateDateAndFormat(startDate, 'yyyy-MM-dd'),
      validateDateAndFormat(endDate, 'yyyy-MM-dd'),
      searchText,
      page
    )
      .then((res) => {
        setAwards(res?.data?.results || []);
        setTotalCount(res?.data?.count || 0);
        setLoading(false);
      })
      .catch((error) => {
        console.log('Error: ', error);
        setLoading(false);
      });
  };

  useEffect(() => {
    setCurrentPage(1);
    reload(1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, category, search]);

  const handleSearchChange = (val) => {
    setSearch(val);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    reload(page, search);
  };

  const handlePrintReport = () => {
    if (isDateRangeInvalid) return;
    setPrinting(true);
    printBacFinalAwardReport(
      category,
      validateDateAndFormat(startDate, 'yyyy-MM-dd'),
      validateDateAndFormat(endDate, 'yyyy-MM-dd'),
      search
    )
      .then((res) => {
        fileDownload(res.data, 'final_award_report.pdf');
        setPrinting(false);
      })
      .catch((error) => {
        console.log(error);
        setPrinting(false);
      });
  };

  return (
    <Box>
      <Typography style={{ fontSize: '1.5rem', padding: '2rem' }}>
        FINAL AWARD REPORT
      </Typography>
      <Box style={{ backgroundColor: 'white' }}>
        <Box style={{ padding: '1rem 1rem 0 1rem', display: 'flex', justifyContent: 'flex-end' }}>
          <GlobalSearch
            value={search}
            onSearchChange={handleSearchChange}
            placeholder="Search by Case ID or Title..."
          />
        </Box>
        <Box className={classes.filtersContainer} style={{ justifyContent: 'space-between' }}>
          <FormControl fullWidth size="small" margin="none" variant="outlined" className={classes.filterSelect}>
            <InputLabel>{'Category'}</InputLabel>
            <Select
              label={'Category'}
              name="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {BAC_CATEGORY_OPTIONS.map((opt, index) => (
                <MenuItem key={index} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DateRangePickerACCB startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} />
          <Button
            className={classes.buttonPrint}
            size="small"
            endIcon={printing ? <CircularProgress size={16} color="inherit" /> : <PrintOutlined />}
            onClick={handlePrintReport}
            disabled={printing}
          >
            {printing ? "Printing..." : "Print Report"}
          </Button>
        </Box>
        <Box>
          <Box style={{ padding: '0.5rem 1.5rem' }}>
            <Typography style={{ fontSize: '0.875rem', color: TEXT.muted, textAlign: "right" }}>
              Total: {totalCount}
            </Typography>
          </Box>
          {loading ? (
            <Box style={{ margin: 'auto', width: '100%', padding: '2rem 0' }}>
              <CircularCenteredLoader />
            </Box>
          ) : (
            awards.length !== 0 ? (
              <>
                <TableContainer component={Paper} style={{ boxShadow: 'none', border: '1px solid #E0E0E0', overflowX: 'auto' }}>
                  <Table size="small" style={{ maxWidth: '100%', marginTop: 0 }}>
                    <TableHead>
                      <TableRow style={{ backgroundColor: '#F2F2F2' }}>
                        {tableHeadings.map((heading, index) => (
                          <TableCell key={index} style={{ fontWeight: 600, color: TEXT.strong, fontSize: '0.875rem', padding: '12px 8px', whiteSpace: 'nowrap' }}>
                            {heading}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {awards.map((award, index) => {
                        const processedAward = processResponse(award, index, currentPage);
                        return (
                          <TableRow key={index} hover style={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}>
                            {processedAward.map((item, idx) => (
                              <TableCell key={idx} style={{ color: TEXT.strong, fontSize: '0.875rem', padding: '12px 8px', whiteSpace: 'pre-line', verticalAlign: 'top' }}>
                                {item}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {!loading && totalCount > PAGE_SIZE && (
                  <Box className={classes.paginationContainer}>
                    <Pagination
                      count={Math.ceil(totalCount / PAGE_SIZE)}
                      page={currentPage}
                      onChange={(e, page) => handlePageChange(page)}
                      color="primary"
                    // shape="rounded"
                    />
                  </Box>
                )}
              </>
            ) : (
              <Grid container justify="center">
                <Grid item>
                  <Box textAlign="center">
                    <Typography style={{ padding: '2rem 8px', textAlign: 'center', fontSize: '1.5rem' }}>
                      No final awards found for this criteria.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
}
